import { Popover, Transition } from "@headlessui/react";
import Prices from "components/Prices";
import { Fragment, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { CartContext, CartContextType } from "containers/PageCheckout/CartContext";
import { normalizeProductImageUrl } from "../../utils/imageUtils";

export default function CartDropdown() {
  const cartContext = useContext(CartContext) as CartContextType;
  const cartItems = cartContext?.cart || [];
  const removeItem = cartContext?.removeFromCart;
  const navigate = useNavigate();

  const renderProduct = (item: any, index: number, close: () => void) => {
    const { name, price, image, id, isRobotics } = item;
    const productDetailLink = isRobotics ? `/product/robotics/${id}` : `/product/${id}`;
    return (
      <div key={index} className="flex py-4 last:pb-0 border-b border-slate-200 dark:border-slate-700 last:border-0">
        <div className="relative h-20 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-slate-50 dark:bg-slate-800">
          <img
            src={normalizeProductImageUrl({ image, images: (item as any).images })}
            alt={name}
            className="h-full w-full object-cover object-center p-1"
          />
          <Link
            onClick={close}
            className="absolute inset-0"
            to={productDetailLink}
            aria-label={`View ${name} product page`}
          />
        </div>

        <div className="ml-3 flex flex-1 flex-col min-w-0">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 break-words line-clamp-2">
                <Link onClick={close} to={productDetailLink}>
                  {name}
                </Link>
              </h3>
            </div>
            <div className="flex-shrink-0">
              <Prices 
                price={price} 
                className="text-sm font-semibold text-slate-900 dark:text-slate-100" 
                isRobotics={isRobotics}
              />
            </div>
          </div>
          <div className="flex flex-1 items-end justify-between text-xs mt-2">
            <p className="text-slate-500 dark:text-slate-400">Qty {item.quantity}</p>
            <button
              type="button"
              className="font-medium text-[#ff6600] hover:text-[#ff4400] transition-colors"
              onClick={() => removeItem?.(id)}
              aria-label={`Remove ${name} from cart`}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className="group w-10 h-10 sm:w-12 sm:h-12 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full inline-flex items-center justify-center focus:outline-none focus:ring-0 focus:ring-offset-0 transition-colors"
            aria-label="Open cart dropdown"
          >
            <div className="w-4 h-4 flex items-center justify-center bg-primary-500 absolute top-1 right-1 rounded-full text-xs text-white font-medium">
              <span>{cartItems.length}</span>
            </div>
            <span style={{ fontSize: '1.2rem' }}>🛒</span>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-20 w-screen max-w-[20rem] sm:max-w-sm px-2 mt-3 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0">
              <div className="rounded-xl shadow-xl ring-1 ring-black/10 dark:ring-white/10 bg-white dark:bg-slate-900 overflow-hidden">
                <div className="max-h-[60vh] sm:max-h-[50vh] min-h-[12rem] p-4 sm:p-5 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                    Shopping Cart
                  </h3>
                  {cartItems.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {cartItems.map((item, index) =>
                          renderProduct(item, index, close)
                        )}
                      </div>
                      <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <ButtonPrimary
                          className="w-full rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium py-2.5 transition-colors"
                          onClick={() => {
                            close();
                            navigate("/cart");
                          }}
                        >
                          Go to Cart
                        </ButtonPrimary>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-center">
                      <p className="text-slate-500 dark:text-slate-400 text-base">
                        Your cart is empty.
                      </p>
                      <ButtonPrimary
                        className="mt-4 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 transition-colors"
                        onClick={() => {
                          close();
                          navigate("/");
                        }}
                      >
                        Start Shopping
                      </ButtonPrimary>
                    </div>
                  )}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}